import { Alert } from "@mui/material";
import { useEffect, useState } from "react";
import {
	fetchPendingRegistrations,
	PENDING_REGISTRATIONS_CHANGED,
	type PendingRegistrations,
} from "../api/pending-registrations";
import { formatRelativeTimeShort } from "../utils/date-formatting";
import { SnoozeButton, useBannerSnoozed } from "./banner-snooze";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * App-wide banner for every member of the Artemis group while a signup waits
 * for approval. The server returns a zero count to everyone else.
 */
export const PendingRegistrationsBanner = () => {
	const [data, setData] = useState<PendingRegistrations | null>(null);

	useEffect(() => {
		let cancelled = false;
		const load = () =>
			fetchPendingRegistrations()
				.then((d) => {
					if (!cancelled) setData(d);
				})
				.catch(() => null);
		load();
		const id = window.setInterval(load, POLL_INTERVAL_MS);
		window.addEventListener(PENDING_REGISTRATIONS_CHANGED, load);
		return () => {
			cancelled = true;
			window.clearInterval(id);
			window.removeEventListener(PENDING_REGISTRATIONS_CHANGED, load);
		};
	}, []);

	// Per count: one more signup brings a snoozed banner back.
	const bannerKey = `pending-registrations:${data?.count}`;
	const snoozed = useBannerSnoozed(bannerKey);

	if (!data || data.count === 0 || snoozed) return null;

	return (
		<Alert
			severity="info"
			data-component="pending-registrations-banner"
			action={<SnoozeButton bannerKey={bannerKey} />}
		>
			{data.count === 1
				? "1 new registration is waiting"
				: `${data.count} new registrations are waiting`}{" "}
			for approval
			{data.oldest_date_joined &&
				`, oldest ${formatRelativeTimeShort(data.oldest_date_joined)} ago`}
			. <a href="/user-management/pending-users">Review</a>
		</Alert>
	);
};
